import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TitleInput from "../../../components/studio/TitleInput.jsx";
import DescriptionInput from "../../../components/studio/DescriptionInput.jsx";
import TagInput from "../../../components/studio/TagInput.jsx";
import AnimationInput from "../../../components/studio/AnimationInput.jsx";
import TheoryInput from "../../../components/studio/TheoryInput.jsx";
import VisualizationTheory from "../../../components/visualizationDetail/VisualizationTheory.jsx";
import {
  getDraft,
  publishVisualization,
  updateVisualization,
} from "../../../services/visualizations.service.js";
import UpdateVisualizationButtons from "../../../components/studio/UpdateVisualizationButtons.jsx";
import PricingSection from "../../../components/studio/PricingSection.jsx";

const UpdateVisualization = () => {
  const { publicationId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [initialError, setInitialError] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState([]);
  const [animationId, setAnimationId] = useState(null);
  const [theory, setTheory] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [pricing, setPricing] = useState({ accessType: "free", price: 0, currency: "USD", pricingModel: "lifetime" });

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        setIsLoading(true);

        const pubRes = await getDraft(publicationId);
        if (cancelled) return;

        setTitle(pubRes.title);
        setDescription(pubRes.description);
        setTags(pubRes.tags);
        setAnimationId(pubRes.animationId);
        setTheory(pubRes.theory);
        setPricing({ accessType: pubRes.accessType || "free", price: pubRes.price || 0, currency: pubRes.currency || "USD", pricingModel: pubRes.pricingModel || "lifetime" });
      } catch (err) {
        if (!cancelled) {
          setInitialError(err.message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [publicationId]);

  const updateHandler = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError("");
    setMessage("");
    try {
      const pubRes = await updateVisualization(publicationId, {
        title,
        description,
        tags,
        animationId,
        theory,
      });
      setTitle(pubRes.title);
      setDescription(pubRes.description);
      setTags(pubRes.tags);
      setAnimationId(pubRes.animationId);
      setTheory(pubRes.theory);
      setMessage("Visualization saved successfully!");
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const updateAndPublishHandler = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError("");
    setMessage("");
    try {
      const pubRes = await updateVisualization(publicationId, {
        title,
        description,
        tags,
        animationId,
        theory,
      });
      setTitle(pubRes.title);
      setDescription(pubRes.description);
      setTags(pubRes.tags);
      setAnimationId(pubRes.animationId);
      setTheory(pubRes.theory);
      await publishVisualization(publicationId);
      navigate(`/visualizations/${publicationId}`);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  if (initialError) {
    return <div className="update-visualization-error">{initialError}</div>;
  }
  return (
    <>
      <TitleInput title={title} setTitle={setTitle} isLoading={isLoading} />
      <DescriptionInput
        description={description}
        setDescription={setDescription}
        isLoading={isLoading}
      />
      <TagInput tags={tags} setTags={setTags} isLoading={isLoading} />
      <AnimationInput
        animationId={animationId}
        setAnimationId={setAnimationId}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
      <TheoryInput
        theory={theory}
        setTheory={setTheory}
        isLoading={isLoading}
      />
      <VisualizationTheory theory={theory} />
      <PricingSection
        contentType="publication"
        contentId={publicationId}
        pricing={pricing}
        setPricing={setPricing}
      />
      <UpdateVisualizationButtons
        error={error}
        message={message}
        updateAndPublishHandler={updateAndPublishHandler}
        updateHandler={updateHandler}
        isLoading={isLoading}
      />
    </>
  );
};

export default UpdateVisualization;
